import { useState, useRef, useEffect } from 'react';
import Modal from '../modal/modal';
import { TextInput } from '../input/inputs';
import { createCamera, updateCamera, discoverOnvif, diagnoseCamera } from '../../services/cameraService';
import { useTranslation } from 'react-i18next';

const EMPTY_FORM = {
    name: '',
    rtsp_url: '',
    onvif_host: '',
    onvif_port: '80',
    rtsp_port: '',
    username: '',
    password: '',
    ptz_profile_token: '',
};

/**
 * Modal for adding or editing an IP camera.
 * Props:
 *   show         - boolean
 *   setter       - fn(bool) to close
 *   camera       - existing camera object (null for create)
 *   projectId    - number
 *   deviceUserId - number|null (machine; if null, shows a dropdown using deviceUsers)
 *   deviceUsers  - array of {id, name} (optional; used when deviceUserId is null)
 *   onSaved      - fn(camera) called after successful save
 */
const extractRtspCredentials = (rtspUrl) => {
    const m = rtspUrl?.match(/^rtsps?:\/\/([^:@]+):([^@]+)@/i);
    return m ? { username: m[1], password: m[2] } : null;
};

function CameraManager({ show, setter, camera, projectId, deviceUserId, deviceUsers, onSaved }) {
    const { t } = useTranslation();
    const [form, setForm] = useState(() => {
        if (!camera) return { ...EMPTY_FORM };
        const embedded = extractRtspCredentials(camera.rtsp_url);
        return {
            name: camera.name || '',
            rtsp_url: camera.rtsp_url || '',
            onvif_host: camera.onvif_host || '',
            onvif_port: String(camera.onvif_port || 80),
            rtsp_port: String(camera.rtsp_port || ''),
            username: embedded ? embedded.username : (camera.username || ''),
            password: embedded ? embedded.password : (camera.password || ''),
            ptz_profile_token: camera.ptz_profile_token || '',
        };
    });

    const mountedRef = useRef(true);
    useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

    const [selectedDeviceUserId, setSelectedDeviceUserId] = useState(
        deviceUserId != null ? deviceUserId : (camera?.device_user ?? '')
    );

    const [useRtsps, setUseRtsps] = useState(() => (camera?.rtsp_url || '').startsWith('rtsps://'));

    const injectRtspCredentials = (rtspUrl, username, password) => {
        if (!rtspUrl) return rtspUrl;
        const m = rtspUrl.match(/^(rtsps?:\/\/)(?:[^@]+@)?(.+)$/i);
        if (!m) return rtspUrl;
        return (username || password) ? `${m[1]}${username}:${password}@${m[2]}` : `${m[1]}${m[2]}`;
    };

    const handleCredentialChange = (field, value) => {
        setForm(f => {
            const newUsername = field === 'username' ? value : f.username;
            const newPassword = field === 'password' ? value : f.password;
            return {
                ...f,
                [field]: value,
                rtsp_url: injectRtspCredentials(f.rtsp_url, newUsername, newPassword),
            };
        });
    };
    const [discoverLoading, setDiscoverLoading] = useState(false);
    const [discoverProfiles, setDiscoverProfiles] = useState([]);
    const [discoverError, setDiscoverError] = useState('');
    const [diagLoading, setDiagLoading] = useState(false);
    const [diagSteps, setDiagSteps] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const handleDiagnose = async () => {
        if (!form.onvif_host) { setDiagSteps([]); return; }
        setDiagLoading(true);
        setDiagSteps([]);
        try {
            const result = await diagnoseCamera(form.onvif_host, parseInt(form.onvif_port) || 80, form.username, form.password);
            if (mountedRef.current) setDiagSteps(result.steps || []);
        } catch (e) {
            if (mountedRef.current) setDiagSteps([{ label: t('common.failed'), ok: false, detail: e?.response?.data?.error || String(e) }]);
        } finally {
            if (mountedRef.current) setDiagLoading(false);
        }
    };

    const handleDiscover = async () => {
        if (!form.onvif_host) {
            setDiscoverError(t('camera.enterOnvifHost'));
            return;
        }
        setDiscoverError('');
        setDiscoverLoading(true);
        setDiscoverProfiles([]);
        try {
            const rtspPort = form.rtsp_port ? parseInt(form.rtsp_port) : undefined;
            const result = await discoverOnvif(form.onvif_host, parseInt(form.onvif_port) || 80, form.username, form.password, rtspPort);
            if (!mountedRef.current) return;
            if (result.profiles && result.profiles.length > 0) {
                setDiscoverProfiles(result.profiles);
            } else {
                setDiscoverError(t('camera.noStreamProfiles'));
            }
        } catch (e) {
            if (mountedRef.current) setDiscoverError(e?.response?.data?.error || t('camera.failedToConnect'));
        } finally {
            if (mountedRef.current) setDiscoverLoading(false);
        }
    };

    const handleSelectProfile = (profile) => {
        let url = profile.rtsp_url;
        if (useRtsps && url.startsWith('rtsp://')) {
            url = 'rtsps://' + url.slice(7);
        }
        setForm(f => ({ ...f, rtsp_url: url, ptz_profile_token: profile.token || '' }));
        setDiscoverProfiles([]);
    };

    const handleSave = async () => {
        if (!form.name) { setError(t('camera.nameRequired')); return; }
        setError('');
        setSaving(true);
        try {
            // If RTSP Port is set, apply it to the RTSP URL (handles port-forwarding scenarios)
            let rtspUrl = form.rtsp_url;
            if (form.rtsp_port && rtspUrl) {
                const m = rtspUrl.match(/^(rtsps?:\/\/(?:[^@]+@)?[^:/]+):\d+(\/.*)?$/i);
                if (m) rtspUrl = m[1] + ':' + form.rtsp_port + (m[2] || '');
            }
            const payload = {
                name: form.name,
                rtsp_url: rtspUrl,
                onvif_host: form.onvif_host,
                onvif_port: parseInt(form.onvif_port) || 80,
                rtsp_port: form.rtsp_port ? parseInt(form.rtsp_port) : null,
                username: form.username,
                password: form.password,
                ptz_profile_token: form.ptz_profile_token || '',
                project: projectId,
                device_user: deviceUserId != null ? deviceUserId : (selectedDeviceUserId || null),
            };
            let saved;
            if (camera) {
                saved = await updateCamera(camera.id, payload);
            } else {
                saved = await createCamera(payload);
            }
            onSaved(saved);
            setter(false);
        } catch (e) {
            if (mountedRef.current) setError(e?.response?.data?.detail || t('camera.failedToSave'));
        } finally {
            if (mountedRef.current) setSaving(false);
        }
    };

    return (
        <Modal
            show={show}
            setter={setter}
            name={camera ? t('camera.editCamera') : t('camera.addCamera')}
            buttonName={saving ? t('common.saving') : (camera ? t('common.save') : t('common.add'))}
            onClick={handleSave}
        >
            <TextInput name={t('camera.fieldName')} internalName="name" form={form} setForm={setForm} placeholder="e.g. Entrance camera" />

            {deviceUserId == null && deviceUsers && (
                <div className="mb-3">
                    <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, display: 'block' }}>
                        {t('camera.selectMachine')}
                    </label>
                    <select
                        className="form-select form-select-sm"
                        value={selectedDeviceUserId}
                        onChange={e => setSelectedDeviceUserId(e.target.value ? parseInt(e.target.value) : '')}
                    >
                        <option value="">{t('camera.noMachine')}</option>
                        {deviceUsers.map(du => (
                            <option key={du.id} value={du.id}>{du.name}</option>
                        ))}
                    </select>
                </div>
            )}

            <TextInput name={t('camera.fieldRtspUrl')} internalName="rtsp_url" form={form} setForm={setForm} placeholder="rtsp[s]://user:password@ip:554/stream" />

            <div className="mt-3 mb-2" style={{ fontSize: 13, color: '#888' }}>
                {t('camera.orDiscoverViaOnvif')}
            </div>

            <div className="d-flex gap-2 align-items-end mb-2">
                <div style={{ flex: 3 }}>
                    <TextInput name={t('camera.fieldOnvifHost')} internalName="onvif_host" form={form} setForm={setForm} placeholder="41.83.242.174" noMargin />
                </div>
                <div style={{ flex: 1 }}>
                    <TextInput name={t('camera.fieldOnvifPort')} internalName="onvif_port" form={form} setForm={setForm} placeholder="80" noMargin />
                </div>
                <div style={{ flex: 1 }}>
                    <TextInput name={t('camera.fieldRtspPort')} internalName="rtsp_port" form={form} setForm={setForm} placeholder="554" noMargin />
                </div>
            </div>

            <div className="d-flex gap-2 mb-2">
                <div style={{ flex: 1 }}>
                    <div className="form-group">
                        <div className="mb-1" style={{ fontSize: 13, fontWeight: 600 }}>{t('camera.fieldUsername')}</div>
                        <input
                            type="text"
                            className="form-control rounded-2"
                            placeholder="admin"
                            value={form.username}
                            onChange={e => handleCredentialChange('username', e.target.value)}
                        />
                    </div>
                </div>
                <div style={{ flex: 1 }}>
                    <div className="form-group">
                        <div className="mb-1" style={{ fontSize: 13, fontWeight: 600 }}>{t('camera.fieldPassword')}</div>
                        <input
                            type="password"
                            className="form-control rounded-2"
                            placeholder=""
                            value={form.password}
                            onChange={e => handleCredentialChange('password', e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <div className="form-check mb-2">
                <input
                    className="form-check-input"
                    type="checkbox"
                    id="useRtsps"
                    checked={useRtsps}
                    onChange={e => setUseRtsps(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="useRtsps" style={{ fontSize: 13 }}>
                    <i className="fas fa-lock me-1" />{t('camera.secureRtsps')}
                </label>
            </div>

            <div className="d-flex gap-2 mb-3">
                <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={handleDiscover}
                    disabled={discoverLoading || diagLoading}
                >
                    {discoverLoading
                        ? <><span className="spinner-border spinner-border-sm me-2" />{t('camera.discovering')}</>
                        : <><i className="fas fa-search me-2" />{t('camera.discoverOnvif')}</>
                    }
                </button>
                <button
                    className="btn btn-outline-warning btn-sm"
                    onClick={handleDiagnose}
                    disabled={discoverLoading || diagLoading}
                >
                    {diagLoading
                        ? <><span className="spinner-border spinner-border-sm me-2" />{t('camera.diagnosing')}</>
                        : <><i className="fas fa-stethoscope me-2" />{t('camera.diagnoseConnection')}</>
                    }
                </button>
            </div>

            {diagSteps.length > 0 && (
                <div className="mb-3 border rounded p-2" style={{ fontSize: 12 }}>
                    {diagSteps.map((s, i) => (
                        <div key={i} className="mb-1">
                            <i className={`fas fa-${s.ok ? 'check-circle text-success' : 'times-circle text-danger'} me-2`} />
                            <strong>{s.label}</strong>
                            <span className="text-muted ms-2">{s.detail}</span>
                        </div>
                    ))}
                </div>
            )}

            {discoverError && (
                <div className="alert alert-warning py-1 px-2 mb-2" style={{ fontSize: 13 }}>
                    {discoverError}
                </div>
            )}

            {discoverProfiles.length > 0 && (
                <div className="mb-3">
                    <div style={{ fontSize: 13, marginBottom: 6 }}>{t('camera.selectProfile')}</div>
                    {discoverProfiles.map((p, i) => (
                        <div key={i} className="mb-2">
                            <button
                                className="btn btn-outline-primary btn-sm me-2"
                                onClick={() => handleSelectProfile(p)}
                            >
                                {p.name}
                            </button>
                            <span style={{ fontSize: 11, color: '#888', wordBreak: 'break-all' }}>
                                {useRtsps && p.rtsp_url.startsWith('rtsp://')
                                    ? 'rtsps://' + p.rtsp_url.slice(7)
                                    : p.rtsp_url}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {error && (
                <div className="alert alert-danger py-1 px-2 mt-2" style={{ fontSize: 13 }}>
                    {error}
                </div>
            )}
        </Modal>
    );
}

export default CameraManager;
